import {
  MarketAccessory,
  MarketFooter,
  MarketButtonGroup,
  MarketButton,
  MarketList,
  MarketPill,
  MarketRow,
  MarketToggle,
  MarketInputSearch,
  MarketActivityIndicator,
  MarketDialog,
} from '@market/react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketLocationPinIcon } from '@squareup/market-react/icons';
import { EnablementStatus } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/enums.js';
import type { FulfillmentConfiguration } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models';
import type { Location } from '@squareup/shared-types-protos/squareup/connect/v2/resources/location';
import { calculateRemainingMinutes } from '../../util/fulfillment-utils.js';
import styles from './location-select-modal.module.css';

/**
 * Map of location IDs to their fulfillment configurations
 * Values may be undefined if the configuration hasn't loaded or failed to load
 */
export type FulfillmentConfigMap = Record<string, FulfillmentConfiguration | undefined>;

export interface LocationSelectModalProps {
  readonly title?: string;
  readonly description?: string;
  readonly locations: Location[];
  readonly locationIdToFulfillmentConfigurationMap?: FulfillmentConfigMap;
  readonly isLoading?: boolean;
  readonly initialSelectedLocationIds: string[];
  readonly onSubmit: (selectedLocationIds: string[]) => Promise<void>;
  readonly onDismiss: () => void;
  readonly onToggleTracking?: (locationId: string, isSelected: boolean) => void;
}

export function LocationSelectModal({
  title,
  description,
  locations,
  locationIdToFulfillmentConfigurationMap = {},
  isLoading = false,
  initialSelectedLocationIds,
  onSubmit,
  onDismiss,
  onToggleTracking,
}: LocationSelectModalProps) {
  const { t } = useTranslation();
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(
    new Set(initialSelectedLocationIds),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasMutated, setHasMutated] = useState(false);

  const getPausedText = (locationId: string): string | null => {
    if (!locationId) {
      return null;
    }

    const configurationForLocation = locationIdToFulfillmentConfigurationMap[locationId];
    if (!configurationForLocation) {
      return null;
    }

    const { pickupSettings, deliverySettings } = configurationForLocation;
    if (pickupSettings?.enablementStatus === EnablementStatus.SCHEDULED && pickupSettings.enablementScheduledAt) {
      const remainingMinutes = calculateRemainingMinutes(pickupSettings.enablementScheduledAt);
      if (remainingMinutes !== null && remainingMinutes > 0) {
        return t('fulfillmentPausedFor', { count: remainingMinutes });
      }
    }

    if (deliverySettings?.enablementStatus === EnablementStatus.SCHEDULED && deliverySettings.enablementScheduledAt) {
      const remainingMinutes = calculateRemainingMinutes(deliverySettings.enablementScheduledAt);
      if (remainingMinutes !== null && remainingMinutes > 0) {
        return t('fulfillmentPausedFor', { count: remainingMinutes });
      }
    }

    return null;
  };

  useEffect(() => {
    // Set selected location ids when locations are loaded, but only if user hasn't made changes yet
    if (locations.length > 0 && !hasMutated) {
      setSelectedLocationIds(new Set(initialSelectedLocationIds));
    }
  }, [locations, initialSelectedLocationIds, hasMutated]);

  const locationIdToNameMap = locations.reduce((acc, loc) => {
    if (loc.id && loc.name) {
      acc[loc.id] = loc.name;
    }
    return acc;
  }, {} as Record<string, string>);

  const handleToggle = (locationId: string, isSelected: boolean) => {
    setHasMutated(true);

    if (onToggleTracking) {
      onToggleTracking(locationId, isSelected);
    }

    setSelectedLocationIds((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(locationId);
      } else {
        newSet.delete(locationId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      await onSubmit(Array.from(selectedLocationIds));
    } finally {
      setIsSaving(false);
    }
  };

  const locationRows = locations.reduce((result, { id: locationId }) => {
    if (!locationId) {
      return result;
    }

    const config = locationIdToFulfillmentConfigurationMap[locationId];
    const pickupScheduled = config?.pickupSettings?.enablementStatus === EnablementStatus.SCHEDULED && !!config?.pickupSettings?.enablementScheduledAt;
    const deliveryScheduled = config?.deliverySettings?.enablementStatus === EnablementStatus.SCHEDULED && !!config?.deliverySettings?.enablementScheduledAt;
    const isEnablementScheduled = pickupScheduled || deliveryScheduled;
    const pausedText = getPausedText(locationId);

    result.push(
      <MarketRow
        key={locationId}
        data-testid={`location-row-${locationId}`}
        value={locationIdToNameMap[locationId]}
        selected={selectedLocationIds.has(locationId)}
        disabled={isEnablementScheduled}
      >
        <MarketAccessory
          size="icon"
          slot="leading-accessory"
        >
          <MarketLocationPinIcon />
        </MarketAccessory>
        <label slot="label">
          {locationIdToNameMap[locationId]}
          {'  '}
          {pausedText && (
            <MarketPill variant="warning">
              {pausedText}
            </MarketPill>
          )}
        </label>
        <MarketToggle
          aria-label={locationIdToNameMap[locationId]}
          slot="control"
          disabled={isEnablementScheduled}
          onMarketToggleChange={(e) => {
            if (isEnablementScheduled) {
              return;
            }
            handleToggle(locationId, e.detail.current);
          }}
        />
      </MarketRow>,
    );
    return result;
  }, [] as React.ReactNode[]);

  return (
    <MarketDialog onMarketDialogDismissed={onDismiss}>
      <h2 className={styles.modalHeader}>{title ?? t('locationSelectModal.title')}</h2>
      <main>
        {description && <p className={styles.modalSubheader}>{description}</p>}
        <MarketList
          multiselect={true}
          className={styles.locationRows}
        >
          <MarketInputSearch
            slot="search"
            placeholder={t('common.search')}
          />
          {!isLoading
            ? locationRows
            : <MarketActivityIndicator data-testid="loading-indicator" />
          }
        </MarketList>
      </main>
      <MarketFooter className={styles.modalFooter}>
        <MarketButtonGroup alignment="fill">
          <MarketButton
            rank="primary"
            isLoading={isSaving}
            onClick={handleSubmit}
          >
            {t('common.save')}
          </MarketButton>
          <MarketButton
            rank="secondary"
            disabled={isSaving}
            onClick={onDismiss}
          >
            {t('common.cancel')}
          </MarketButton>
        </MarketButtonGroup>
      </MarketFooter>
    </MarketDialog>
  );
}
